import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Switch } from "@progress/kendo-react-inputs";
import { arrowLeftIcon, arrowRightIcon, downloadIcon, envelopeIcon, hyperlinkOpenIcon, saveIcon, trashIcon, xIcon } from "@progress/kendo-svg-icons";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { openWindowPerc } from "services/openWindow";
import { OrderImage } from "TypeGen/Order/Images/order-image";

interface Props {
  images: OrderImage[];
  paperworkTypes: { ID: number; Name: string }[];
  startIndex: number;
  onClose: () => void;
  onUpdate: (imageId: number, data: { TypeOfPW: number; PrintFlag: boolean }) => Promise<boolean>;
  onDelete: (imageId: number) => Promise<boolean>;
  onEmail: (imageId: number) => void;
}

const ZOOM_SCALE = 2;
const DRAG_THRESHOLD = 4;

const ImageLightbox = ({ images, paperworkTypes, startIndex, onClose, onUpdate, onDelete, onEmail }: Props) => {
  const [index, setIndex] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [imgLoading, setImgLoading] = useState(true);
  const [isPanning, setIsPanning] = useState(false);
  const [paperworkType, setPaperworkType] = useState(images[startIndex].TypeOfPW);
  const [printWithInvoice, setPrintWithInvoice] = useState(images[startIndex].PrintFlag);
  const [saving, setSaving] = useState(false);

  // Drag tracking (refs so intra-drag reads don't need a render)
  const dragged = useRef(false);
  const start = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  // Wheel throttle: one image per scroll gesture (ignore trackpad momentum)
  const wheelLock = useRef(false);
  const wheelTimer = useRef<number>();

  const image = images[index];
  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;
  const edited = paperworkType !== image.TypeOfPW || printWithInvoice !== image.PrintFlag;

  const resetView = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const goPrev = useCallback(() => {
    if (index <= 0) return;
    const previousImage = images[index - 1];
    resetView();
    setImgLoading(true);
    setPaperworkType(previousImage.TypeOfPW);
    setPrintWithInvoice(previousImage.PrintFlag);
    setIndex((i) => i - 1);
  }, [images, index, resetView]);

  const goNext = useCallback(() => {
    if (index >= images.length - 1) return;
    const nextImage = images[index + 1];
    resetView();
    setImgLoading(true);
    setPaperworkType(nextImage.TypeOfPW);
    setPrintWithInvoice(nextImage.PrintFlag);
    setIndex((i) => i + 1);
  }, [images, index, resetView]);

  useHotkeys("left", goPrev, { enabled: hasPrev, preventDefault: true }, [goPrev, hasPrev]);
  useHotkeys("right", goNext, { enabled: hasNext, preventDefault: true }, [goNext, hasNext]);
  useHotkeys("esc", onClose, { preventDefault: true }, [onClose]);

  // Lock background scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Prefetch neighbors so arrow-key nav is instant. Deferred until the visible
  // image finishes loading (called from its onLoad) so it never competes with
  // the image the user is actually waiting on.
  const prefetchNeighbors = useCallback(() => {
    [index - 1, index + 1].forEach((i) => {
      if (i >= 0 && i < images.length) {
        const img = new Image();
        img.src = `/Image/View/${images[i].ImageID}`;
      }
    });
  }, [index, images]);

  const toggleZoom = () => {
    if (dragged.current) return; // this was a pan, not a click
    if (scale === 1) {
      setScale(ZOOM_SCALE);
    } else {
      resetView();
    }
  };

  const onMouseDown = (e: React.MouseEvent) => {
    dragged.current = false;
    if (scale === 1) return;
    setIsPanning(true);
    start.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isPanning) return;
    const dx = e.clientX - start.current.x;
    const dy = e.clientY - start.current.y;
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) dragged.current = true;
    setOffset({ x: start.current.ox + dx, y: start.current.oy + dy });
  };

  const endPan = () => { setIsPanning(false); };

  const onWheel = (e: React.WheelEvent) => {
    if (e.deltaY === 0) return;
    if (!wheelLock.current) {
      if (e.deltaY > 0) goNext();
      else goPrev();
      wheelLock.current = true;
    }
    // Hold the lock until scrolling (incl. momentum) stops for a beat
    if (wheelTimer.current) window.clearTimeout(wheelTimer.current);
    wheelTimer.current = window.setTimeout(() => { wheelLock.current = false; }, 200);
  };

  const updateImage = async () => {
    setSaving(true);
    try {
      await onUpdate(image.ImageID, {
        TypeOfPW: paperworkType,
        PrintFlag: printWithInvoice,
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteImage = async () => {
    setSaving(true);
    try {
      if (await onDelete(image.ImageID)) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10500,
        display: "flex",
        background: "rgba(0,0,0,0.92)",
      }}
    >
      {/* Image stage */}
      <div
        onClick={onClose}
        onWheel={onWheel}
        onMouseMove={onMouseMove}
        onMouseUp={endPan}
        onMouseLeave={endPan}
        style={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {imgLoading && (
          <span style={{ position: "absolute", color: "#ccc", fontSize: 14 }}>Loading…</span>
        )}
        <img
          key={image.ImageID}
          src={`/Image/View/${image.ImageID}`}
          fetchPriority="high"
          onLoad={() => { setImgLoading(false); prefetchNeighbors(); }}
          onClick={(e) => { e.stopPropagation(); toggleZoom(); }}
          onMouseDown={onMouseDown}
          draggable={false}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transition: isPanning ? "none" : "transform 0.15s ease",
            cursor: scale === 1 ? "zoom-in" : isPanning ? "grabbing" : "grab",
            userSelect: "none",
          }}
        />

        {/* Close */}
        <Button
          fillMode="flat"
          svgIcon={xIcon}
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          style={{ position: "absolute", top: 12, right: 12, color: "#fff" }}
          title="Close (Esc)"
        />

        {/* Prev / Next */}
        {hasPrev && (
          <Button
            fillMode="flat"
            svgIcon={arrowLeftIcon}
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#fff" }}
            title="Previous (←)"
          />
        )}
        {hasNext && (
          <Button
            fillMode="flat"
            svgIcon={arrowRightIcon}
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#fff" }}
            title="Next (→)"
          />
        )}
      </div>

      {/* Attribute panel */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 320,
          flexShrink: 0,
          background: "var(--kendo-color-surface-alt, #fff)",
          color: "var(--kendo-color-on-app-surface, inherit)",
          height: "100%",
          overflowY: "auto",
          padding: "16px 20px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0">{image.ImageID}-{image.PageNum}</h5>
          <span className="text-muted">{index + 1} of {images.length}</span>
        </div>

        <div className="mb-3">
          <label className="d-block font-weight-bold mb-1">Document Type</label>
          <DropDownList
            disabled={saving}
            data={paperworkTypes}
            value={paperworkTypes.find((x) => x.ID === paperworkType)}
            textField="Name"
            dataItemKey="ID"
            onChange={(e) => setPaperworkType(e.target.value.ID)}
            style={{ width: "100%" }}
          />
        </div>

        <div className="d-flex align-items-center justify-content-between mb-3">
          <label className="font-weight-bold mb-0">Print w/ Invoice</label>
          <Switch
            disabled={saving}
            checked={printWithInvoice}
            onChange={(e) => setPrintWithInvoice(e.value)}
            size="small"
          />
        </div>

        <Button
          themeColor="primary"
          svgIcon={saveIcon}
          disabled={saving || !edited}
          onClick={updateImage}
          className="mb-3 w-100"
        >
          {saving ? "Saving..." : "Update"}
        </Button>

        <dl className="mb-3">
          <dt>Indexed</dt>
          <dd>{image.ImageDate ? dayjs.utc(image.ImageDate).format("MM/DD/YYYY") : "—"}</dd>
          <dt>Source</dt>
          <dd>{image.ImageSourceName || "—"}</dd>
          <dt>Modified By</dt>
          <dd>{image.ModifiedByUserNumber || "—"}</dd>
          <dt>Modified Date</dt>
          <dd>{image.ModifiedDateTime ? dayjs.utc(image.ModifiedDateTime).tz("America/New_York").format("MM/DD/YYYY HH:mm") : "—"}</dd>
          {image.AIClassifiedReason && (
            <>
              <dt>AI Summary</dt>
              <dd>{image.AIClassifiedReason}</dd>
            </>
          )}
        </dl>

        <div className="d-flex flex-column">
          <a className="btn btn-primary mb-2" href={`/Image/Download/${image.ImageID}`}>
            <SvgIcon icon={downloadIcon} className="mr-1" /> Download
          </a>
          <Button
            svgIcon={hyperlinkOpenIcon}
            className="mb-2"
            onClick={() => openWindowPerc(`/Image/View/${image.ImageID}`, .9, .85, "ClientApp-Image")}
          >
            Open in new tab
          </Button>
          <Button
            svgIcon={envelopeIcon}
            className="mb-2"
            disabled={saving}
            onClick={() => {
              onEmail(image.ImageID);
              onClose();
            }}
          >
            E-Mail
          </Button>
          <Button
            themeColor="error"
            svgIcon={trashIcon}
            disabled={saving}
            onClick={deleteImage}
          >
            Delete Image
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImageLightbox;
